"use client"

import { ControlledModal } from "@/components"
import { IconButton, RadioButton, Text, TextArea } from "@/elements"
import { graphql } from "@/gql"
import { JobPostingReportReason, ReportJobPostingDocument } from "@/gql/graphql"
import { FlagIcon } from "@/icons/outline"
import { useMutation } from "@apollo/client"
import { FC, useCallback, useEffect, useState } from "react"
import { toast } from "@/components/Toast"
import { IconButtonVariant } from "@/elements/IconButton/useIconButtonStyles"

graphql(`
  mutation ReportJobPosting($input: CreateJobPostingReportInput!) {
    createJobPostingReport(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`)

interface Props {
  jobPostingId: string | undefined
  buttonVariant?: IconButtonVariant
}

export const ReportJobPostingModal: FC<Props> = props => {
  const { jobPostingId, buttonVariant = "white" } = props
  const [isOpen, setIsOpen] = useState(false)
  const [reason, setReason] = useState<JobPostingReportReason | undefined>(undefined)
  const [details, setDetails] = useState("")
  const [reportJobPosting, { loading }] = useMutation(ReportJobPostingDocument)

  const handleReportJobPosting = useCallback(async () => {
    if (!jobPostingId) {
      toast.error("Error finding job to report")
      return
    }
    if (!reason) {
      toast.error("Please select a reason for reporting")
      return
    }

    try {
      await reportJobPosting({
        variables: { input: { jobPosting: jobPostingId, reason, details: details || undefined } },
      })
      setIsOpen(false)
      toast.success("Job posting reported successfully")
    } catch (error) {
      toast.error("Failed to report job posting")
    }
  }, [jobPostingId, reason, details, reportJobPosting])

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setReason(undefined)
        setDetails("")
      }, 200)
    }
  }, [isOpen])

  return (
    <>
      <IconButton Icon={FlagIcon} onClick={() => setIsOpen(true)} variant={buttonVariant} />
      <ControlledModal
        open={isOpen}
        onOpenChange={setIsOpen}
        variant="default"
        header={{
          title: "Report Job Posting",
          subtitle: "Please select a reason for reporting:",
          variant: "warning",
        }}
        zIndex={102}
        body={
          <div className="flex flex-col gap-y-7">
            <div className="flex flex-col gap-y-4">
              <div className="flex flex-col gap-2">
                <RadioButton
                  name="reportReason"
                  label="Posting contains objectionable content"
                  onChange={() => setReason(JobPostingReportReason.ObjectionableContent)}
                  checked={reason === JobPostingReportReason.ObjectionableContent}
                  value={JobPostingReportReason.ObjectionableContent}
                />
                <RadioButton
                  name="reportReason"
                  label="Spam"
                  onChange={() => setReason(JobPostingReportReason.Spam)}
                  checked={reason === JobPostingReportReason.Spam}
                  value={JobPostingReportReason.Spam}
                />
                <RadioButton
                  name="reportReason"
                  label="Something else"
                  onChange={() => setReason(JobPostingReportReason.Other)}
                  checked={reason === JobPostingReportReason.Other}
                  value={JobPostingReportReason.Other}
                />
              </div>
            </div>

            <div className="flex flex-col gap-y-2">
              <Text className="text-fg-2">Additional details (optional):</Text>
              <TextArea
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder="Please provide any additional information..."
                rows={4}
                className="w-full"
              />
            </div>
          </div>
        }
        footer={{
          primaryButton: {
            children: "Submit Report",
            onClick: handleReportJobPosting,
            loading: loading,
          },
          tertiaryButton: {
            children: "Cancel",
            onClick: () => setIsOpen(false),
          },
        }}
      />{" "}
    </>
  )
}
